import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { fmtEur } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle } from "lucide-react";

export function RendicontoTab({ companyId, year }) {
  const [method, setMethod] = useState("indiretto");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/companies/${companyId}/cashflow`, { params: { year, method } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [companyId, year, method]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="font-heading font-semibold text-lg tracking-tight">Rendiconto Finanziario</h3>
          <p className="text-sm text-muted-foreground">Secondo OIC 10 · Esercizio {data?.fiscal_year || year}</p>
        </div>
        <div className="inline-flex rounded-lg border border-border p-1 bg-secondary/40">
          {["indiretto", "diretto"].map((m) => (
            <button
              key={m}
              data-testid={`cashflow-method-${m}`}
              onClick={() => setMethod(m)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${
                method === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Metodo {m}
            </button>
          ))}
        </div>
      </div>

      {!data?.has_prev && !loading && (
        <div className="flex items-start gap-2 text-sm text-amber-500 bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-3 mb-6">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Manca l'esercizio precedente: le variazioni del capitale circolante e i flussi di investimento/finanziamento non sono calcolabili. Carica il bilancio dell'anno precedente per il rendiconto completo.</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Calcolo in corso...
        </div>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={method}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="grid grid-cols-1 lg:grid-cols-4 gap-4"
          >
            <div className="lg:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-4 mb-2">
              {[
                ["Gestione Operativa (A)", data?.totals?.operativo],
                ["Investimenti (B)", data?.totals?.investimento],
                ["Finanziamenti (C)", data?.totals?.finanziamento],
                ["Flusso Totale (A+B+C)", data?.totals?.totale],
              ].map(([label, val]) => (
                <Card key={label} className="p-4">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
                  <div className={`font-mono font-bold text-xl mt-1 ${(val ?? 0) < 0 ? "text-red-500" : "text-emerald-500"}`}>
                    {fmtEur(val)}
                  </div>
                </Card>
              ))}
            </div>

            {data?.sections?.map((sec, si) => (
              <Card key={si} className="lg:col-span-4 p-0 overflow-hidden">
                <div className="px-5 py-3 border-b border-border/60 bg-secondary/50">
                  <h4 className="font-heading font-semibold text-sm">{sec.title}</h4>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {sec.rows.map((r, ri) => (
                      <tr key={ri} className={`border-b border-border/40 last:border-0 ${r.total ? "bg-accent/50" : r.bold ? "bg-accent/20" : "hover:bg-accent/20"}`}>
                        <td className={`px-5 py-2.5 ${r.bold ? "font-semibold" : ""}`}>{r.label}</td>
                        <td className={`px-5 py-2.5 text-right font-mono ${r.bold ? "font-semibold" : ""} ${(r.value ?? 0) < 0 ? "text-red-500" : ""}`}>
                          {fmtEur(r.value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            ))}
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}
